import { createContext, useContext, useState } from 'react';
import { ParserBuilder, Values, parseAsArrayOf, parseAsInteger, useQueryState } from 'nuqs';
import { MentorshipStatusEnum, Tag } from '@juno/client-api/model';
import {
  BackendFiltersOptions,
  Connector,
  TagsFilter,
  appendTagFilterUtil,
  createBackendFilters,
  createFilters,
  filter,
  pageFilter,
  removeTagFilterUtil,
  sortFilter,
  tagsFilter,
  useQueryFilters,
} from '@juno/utils';

type DirectoryFilters = Values<{
  tags: ParserBuilder<TagsFilter>;
  mentorship_status: ParserBuilder<number[]>;
}>;

export const SAVED_PAGE_SIZE = 5;

export interface DirectoryFiltersContextProps {
  // Tags
  appendTagFilter: (tag: Tag) => void;
  removeTagFilter: (tag: Tag) => void;
  clearTagsFilter: () => void;
  // Mentorship
  appendMentorshipFilter: (status: MentorshipStatusEnum) => void;
  removeMentorshipFilter: (status: MentorshipStatusEnum) => void;
  clearMentorshipFilter: () => void;
  filters: DirectoryFilters;
  buildBackendFilters: (
    options?: BackendFiltersOptions,
  ) => Record<Connector, Record<string, unknown>>;
  sort: DirectorySort;
  setSort: (value: DirectorySort) => void;
  page: number;
  setPage: (value: number) => void;
  resetPage: () => void;
  savedUsersPage: number;
  setSavedUsersPage: (value: number) => void;
}

export const DirectoryFiltersContext = createContext<DirectoryFiltersContextProps | undefined>(
  undefined,
);

export const DirectorySort = {
  FIRSTNAME: 'first_name',
  LASTNAME: 'last_name',
} as const;

export type DirectorySort = (typeof DirectorySort)[keyof typeof DirectorySort];

export const DirectoryFiltersProvider = ({ children }: { children: React.ReactNode }) => {
  const [savedUsersPage, setSavedUsersPage] = useState(1);

  const filtersConfig = createFilters(
    tagsFilter(),
    filter('mentorship_status', {
      lookup: 'mentorship_status__in',
      parser: parseAsArrayOf(parseAsInteger).withDefault([]),
      connector: 'filter_or',
    }),
    sortFilter({ default: [DirectorySort.FIRSTNAME] }),
    pageFilter(),
  );

  const { filters, setFilters } = useQueryFilters(filtersConfig);
  const sort = filters.sort?.[0] as DirectorySort;
  const page = filters.page || 1;

  const appendTagFilter = (tag: Tag) => {
    setFilters((prevFilters) => appendTagFilterUtil(prevFilters, tag));
  };

  const removeTagFilter = (tag: Tag) => {
    setFilters((prevFilters) => removeTagFilterUtil(prevFilters, tag));
  };

  const clearTagsFilter = () => {
    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        tags: [],
      };
    });
  };

  const appendMentorshipFilter = (status: MentorshipStatusEnum) => {
    setFilters((prevFilters) => {
      const mentorshipStatus = prevFilters.mentorship_status || [];
      if (!mentorshipStatus.includes(status)) {
        return {
          ...prevFilters,
          mentorship_status: [...mentorshipStatus, status],
        };
      }
      return prevFilters;
    });
  };

  const removeMentorshipFilter = (status: MentorshipStatusEnum) => {
    setFilters((prevFilters) => {
      const mentorshipStatus = prevFilters.mentorship_status || [];
      return {
        ...prevFilters,
        mentorship_status: mentorshipStatus.filter((s) => s !== status),
      };
    });
  };

  const clearMentorshipFilter = () => {
    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        mentorship_status: [],
      };
    });
  };

  const setSort = (value: DirectorySort) => {
    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        sort: [value],
      };
    });
  };

  const setPage = (value: number) => {
    setFilters((prevFilters) => {
      return {
        ...prevFilters,
        page: value,
      };
    });
  };

  const resetPage = () => {
    setPage(1);
  };

  const buildBackendFilters = (options?: BackendFiltersOptions) => {
    return createBackendFilters(filtersConfig.defs, filters, options);
  };

  return (
    <DirectoryFiltersContext.Provider
      value={{
        filters,
        buildBackendFilters,
        appendTagFilter,
        removeTagFilter,
        clearTagsFilter,
        appendMentorshipFilter,
        removeMentorshipFilter,
        clearMentorshipFilter,
        sort,
        setSort,
        page,
        setPage,
        resetPage,
        savedUsersPage,
        setSavedUsersPage,
      }}
    >
      {children}
    </DirectoryFiltersContext.Provider>
  );
};

export const useDirectoryFilters = () => {
  const context = useContext(DirectoryFiltersContext);

  if (!context) {
    throw new Error('useDirectoryFilters must be used within a DirectoryFiltersProvider');
  }

  return context;
};
